import React, { useState, memo, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import './IntegrationVideoEmbed.scss';

const IntegrationVideoEmbed = memo(({ config, className = '' }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);

  const autoplay = useMemo(
    () => (config.autoplay !== undefined ? config.autoplay : 1),
    [config.autoplay]
  );
  const height = useMemo(() => config.height || 450, [config.height]);

  const handleVideoLoad = () => {
    setIsLoaded(true);
    setIsLoading(false);
  };

  const handleVideoError = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (videoRef.current && videoRef.current.src) {
      const currentSrc = videoRef.current.src;
      const isYoutubeMatch =
        config.youtubeVideoId && currentSrc.includes(config.youtubeVideoId);
      const isDirectMatch = config.videoSrc && currentSrc === config.videoSrc;

      if (!isYoutubeMatch && !isDirectMatch) {
        setIsLoaded(false);
        setIsLoading(true);
      }
    }
  }, [config.videoSrc, config.youtubeVideoId]);

  if (config.youtubeVideoId) {
    return (
      <div className={`integration-video-embed ${className}`.trim()}>
        {isLoading && (
          <div className='integration-video-embed__loading'>
            <CircularProgress size={40} />
          </div>
        )}
        <iframe
          height={height}
          ref={videoRef}
          className={`integration-video-embed__content ${
            isLoaded ? 'integration-video-embed__content--loaded' : ''
          }`}
          src={`https://www.youtube-nocookie.com/embed/${config.youtubeVideoId}?autoplay=${autoplay}&rel=0&controls=2&modestbranding=1&showinfo=0&iv_load_policy=3`}
          title={config.title}
          frameBorder='0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
          onLoad={handleVideoLoad}
          onError={handleVideoError}
        />
      </div>
    );
  }

  if (config.videoSrc) {
    return (
      <div className={`integration-video-embed ${className}`.trim()}>
        {isLoading && (
          <div className='integration-video-embed__loading'>
            <CircularProgress size={40} />
          </div>
        )}
        <video
          role='application'
          ref={videoRef}
          className={`integration-video-embed__content ${
            isLoaded ? 'integration-video-embed__content--loaded' : ''
          }`}
          autoPlay={autoplay === 1}
          loop
          muted
          playsInline
          onLoadedData={handleVideoLoad}
        >
          <source src={config.videoSrc} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  return null;
});

IntegrationVideoEmbed.propTypes = {
  config: PropTypes.shape({
    youtubeVideoId: PropTypes.string,
    videoSrc: PropTypes.string,
    title: PropTypes.string,
    autoplay: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  className: PropTypes.string
};

export default IntegrationVideoEmbed;
